"use client";

import { useState, type FormEvent } from "react";

import { Button } from "@/components/ui/Button";

type Cargo =
  | "diretor_tecnico"
  | "gestor_ti"
  | "compliance"
  | "administrador"
  | "medico_responsavel"
  | "outro"
  | "";

type Status = "idle" | "submitting" | "success" | "error";

const CARGO_OPTIONS: { value: Cargo; label: string }[] = [
  { value: "", label: "Cargo (opcional)" },
  { value: "diretor_tecnico", label: "Diretor Técnico" },
  { value: "gestor_ti", label: "Gestor de TI" },
  { value: "compliance", label: "Compliance / DPO" },
  { value: "administrador", label: "Administrador hospitalar" },
  { value: "medico_responsavel", label: "Médico responsável" },
  { value: "outro", label: "Outro" },
];

export function LeadFormCfm({ formId }: { formId: string }) {
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string>("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (status === "submitting") return;

    const form = e.currentTarget;
    const data = new FormData(form);
    const payload = {
      name: String(data.get("name") ?? "").trim(),
      email: String(data.get("email") ?? "").trim().toLowerCase(),
      institution: String(data.get("institution") ?? "").trim(),
      role: String(data.get("role") ?? "").trim(),
      website: String(data.get("website") ?? ""), // honeypot
    };

    setStatus("submitting");
    setError("");

    try {
      const res = await fetch("/api/saude-cfm-2454", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? "Não foi possível registrar agora.");
      }

      setStatus("success");
      form.reset();
    } catch (err) {
      setStatus("error");
      setError(err instanceof Error ? err.message : "Erro inesperado.");
    }
  }

  if (status === "success") {
    return (
      <div
        role="status"
        className="rounded-2xl border border-emerald-400/30 bg-emerald-500/10 p-6 text-emerald-100"
      >
        <p className="font-semibold">Checklist enviado.</p>
        <p className="mt-2 text-sm text-emerald-100/80">
          Enviamos o material para o e-mail informado. Se não chegar em
          alguns minutos, verifique o spam ou tente outro endereço
          corporativo.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      {/* honeypot anti-spam — invisível pra humanos */}
      <div aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
        <label htmlFor={`${formId}-website`}>Não preencher</label>
        <input
          id={`${formId}-website`}
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
        />
      </div>

      <div>
        <label htmlFor={`${formId}-name`} className="block text-sm font-semibold text-white">
          Nome
        </label>
        <input
          id={`${formId}-name`}
          name="name"
          type="text"
          required
          autoComplete="name"
          className="mt-2 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-base text-white placeholder:text-mist/60 focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        />
      </div>

      <div>
        <label htmlFor={`${formId}-email`} className="block text-sm font-semibold text-white">
          E-mail corporativo
        </label>
        <input
          id={`${formId}-email`}
          name="email"
          type="email"
          required
          autoComplete="email"
          className="mt-2 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-base text-white placeholder:text-mist/60 focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        />
      </div>

      <div>
        <label htmlFor={`${formId}-institution`} className="block text-sm font-semibold text-white">
          Nome da instituição
        </label>
        <input
          id={`${formId}-institution`}
          name="institution"
          type="text"
          required
          autoComplete="organization"
          className="mt-2 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-base text-white placeholder:text-mist/60 focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        />
      </div>

      <div>
        <label htmlFor={`${formId}-role`} className="block text-sm font-semibold text-white">
          Cargo
        </label>
        <select
          id={`${formId}-role`}
          name="role"
          defaultValue=""
          className="mt-2 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-base text-white focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          {CARGO_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value} className="bg-slate-900">
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <p role="alert" className="rounded-xl border border-red-400/30 bg-red-500/10 p-3 text-sm text-red-100">
          {error}
        </p>
      )}

      <Button type="submit" disabled={status === "submitting"} className="w-full">
        {status === "submitting" ? "Enviando..." : "Receber checklist técnico"}
      </Button>

      <p className="text-xs text-mist/80">
        Usaremos seus dados para enviar o checklist e comunicações sobre
        adequação à CFM 2.454. Você pode descadastrar a qualquer momento.
      </p>
    </form>
  );
}
